/**
 * Winner announcement component
 * Shows the winner with epic reveal animation
 */

import type { WinnerInfo, PrizeInfo } from "@/lib/websocket";
import styles from "./WinnerAnnouncement.module.css";

// =============================================================================
// Types
// =============================================================================

interface WinnerAnnouncementProps {
  winner: WinnerInfo | null;
  prize: PrizeInfo | null;
  isVisible: boolean;
  size?: "normal" | "large";
}

// =============================================================================
// Component
// =============================================================================

export function WinnerAnnouncement({
  winner,
  prize,
  isVisible,
  size = "normal",
}: WinnerAnnouncementProps) {
  if (!winner || !prize || !isVisible) {
    return null;
  }

  return (
    <div className={`${styles.container} ${styles[size]}`}>
      {/* Background glow */}
      <div className={styles.glow} />

      {/* Main content */}
      <div className={styles.content}>
        {/* Trophy icon */}
        <div className={styles.trophy}>🏆</div>

        {/* Congratulations text */}
        <div className={styles.congratsText}>ПОЗДРАВЛЯЕМ!</div>

        {/* Winner info */}
        <div className={styles.winnerInfo}>
          {/* Ticket number */}
          <div className={styles.ticketNumber}>
            <span className={styles.ticketLabel}>Билет</span>
            <span className={styles.ticketValue}>#{winner.ticketNumber}</span>
          </div>

          {/* Winner name */}
          <div className={styles.winnerName}>{winner.name}</div>

          {/* Telegram username */}
          {winner.username && (
            <div className={styles.winnerUsername}>@{winner.username}</div>
          )}
        </div>

        {/* Prize info */}
        <div className={styles.prizeInfo}>
          <div className={styles.prizeLabel}>Выиграл(а):</div>
          <div className={styles.prizeName}>{prize.name}</div>
          {prize.imageUrl && (
            <div className={styles.prizeImageContainer}>
              <img
                src={prize.imageUrl}
                alt={prize.name}
                className={styles.prizeImage}
              />
            </div>
          )}
        </div>
      </div>

      {/* Decorative rays */}
      <div className={styles.rays}>
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className={styles.ray}
            style={{ "--angle": `${i * 30}deg` } as React.CSSProperties}
          />
        ))}
      </div>
    </div>
  );
}

export default WinnerAnnouncement;
